import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, generatePdf } from '../lib/api';
import type { TemplateRow } from '../lib/types';

export default function Dashboard() {
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [connectors, setConnectors] = useState<any[]>([]);
  const [stats, setStats] = useState<{ total: number; success: number; failed: number; last_24h: number } | null>(null);
  const [health, setHealth] = useState<{ status: string; environment: string } | null>(null);
  const [testData, setTestData] = useState('{\n  "customer": "Acme Inc.",\n  "amount": 1234.56\n}');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    api.listTemplates().then((t) => {
      setTemplates(t);
      if (t[0]) setSelectedTemplate(t[0].id);
    }).catch(console.error);
    api.listConnectors().then(setConnectors).catch(console.error);
    api.generationStats().then(setStats).catch(() => {});
    api.health().then(setHealth).catch(() => {});
  }, []);

  async function runQuickTest() {
    if (!selectedTemplate) return;
    setTesting(true);
    setTestResult(null);
    try {
      let data: any;
      try { data = JSON.parse(testData); } catch { throw new Error('Test data is not valid JSON'); }
      const { blob, generationId } = await generatePdf({ template_id: selectedTemplate, data });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTestResult(`OK: ${blob.size} bytes, generation ${generationId}`);
    } catch (e) {
      setTestResult(`Failed: ${(e as Error).message}`);
    } finally {
      setTesting(false);
    }
  }

  return (
    <div>
      <div className="cols-3" style={{ marginBottom: 16 }}>
        <div className="kpi accent">
          <div className="label">Templates</div>
          <div className="value">{templates.length}</div>
        </div>
        <div className="kpi">
          <div className="label">Connectors</div>
          <div className="value">{connectors.length}</div>
        </div>
        <div className="kpi success">
          <div className="label">Generations (24h)</div>
          <div className="value">{stats?.last_24h ?? '–'}</div>
        </div>
      </div>

      <div className="cols-2">
        <div className="card">
          <h3>Service status</h3>
          {health ? (
            <div className="kv">
              <dt>Status</dt><dd><span className="pill success">{health.status}</span></dd>
              <dt>Environment</dt><dd>{health.environment}</dd>
              <dt>API base</dt><dd>/api/v1</dd>
            </div>
          ) : (
            <p className="muted">Loading...</p>
          )}
        </div>
        <div className="card">
          <h3>Quick start</h3>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li><Link to="/templates/new">Create a template</Link> in the visual designer</li>
            <li><Link to="/connectors/new">Add a connector</Link> for your ERP/CRM</li>
            <li>Configure your ERP to call <code>POST /api/v1/generate</code> with the connector's API key</li>
            <li>Check <Link to="/logs">logs</Link> to confirm PDFs are flowing</li>
          </ol>
        </div>
      </div>

      {templates.length > 0 && (
        <div className="card">
          <h3>Quick test</h3>
          <p className="muted">Generate a PDF in the browser to verify a template works end-to-end.</p>
          <div className="field">
            <label>Template</label>
            <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Data (JSON)</label>
            <textarea value={testData} onChange={(e) => setTestData(e.target.value)} />
          </div>
          <div className="row">
            <button onClick={runQuickTest} disabled={testing || !selectedTemplate}>
              {testing ? 'Generating…' : 'Generate & open'}
            </button>
            {testResult && <span className="muted mono">{testResult}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
